/**
 * Utility to unregister all service workers and clean up PWA remnants
 */
export async function unregisterServiceWorkers() {
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      console.log(`Found ${registrations.length} service worker registrations to clean up`);
      
      for (let registration of registrations) {
        const result = await registration.unregister();
        console.log(`Unregistered service worker for scope: ${registration.scope}, success: ${result}`);
      }
      
      return registrations.length > 0;
    } catch (error) {
      console.error('Error unregistering service workers:', error);
      return false;
    }
  }
  
  return false;
}

/**
 * Clear targeted browser caches that may be causing gallery issues
 */
export async function clearTargetedCaches() {
  if ('caches' in window) {
    try {
      const cacheNames = await window.caches.keys();
      console.log(`Found ${cacheNames.length} caches to evaluate`);
      
      // Only clear caches related to our app and storage
      const cachesToClear = cacheNames.filter(name => 
        name.includes('workbox') || 
        name.includes('supabase') || 
        name.includes('gallery') || 
        name.includes('pwa') ||
        name.includes('hot-rod')
      );
      
      console.log(`Will clear ${cachesToClear.length} targeted caches`);
      
      await Promise.all(
        cachesToClear.map(cacheName => {
          console.log(`Deleting cache: ${cacheName}`);
          return window.caches.delete(cacheName);
        })
      );
      
      return cachesToClear.length > 0;
    } catch (error) {
      console.error('Error clearing targeted caches:', error);
      return false;
    }
  }
  
  return false;
}

/**
 * Clear localStorage entries that might be affecting gallery
 */
export function clearLocalStorageCache() {
  try {
    let clearedItems = 0;
    
    // Clear only localStorage items related to the gallery and storage
    Object.keys(localStorage).forEach(key => {
      if (
        key.includes('gallery') ||
        key.includes('photo') ||
        key.includes('video') ||
        key.includes('supabase-storage') ||
        key.includes('pwa')
      ) {
        localStorage.removeItem(key);
        clearedItems++;
      }
    });
    
    console.log(`Cleared ${clearedItems} localStorage items`);
    return clearedItems > 0;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
}

/**
 * Comprehensive PWA cleanup function with conditional execution
 */
export async function cleanupPWA() {
  // Check if we've already run the cleanup recently
  const lastCleanup = localStorage.getItem('pwa_cleanup_timestamp');
  const now = Date.now();
  
  // If we've run cleanup in the last hour, skip it
  if (lastCleanup && (now - parseInt(lastCleanup, 10)) < 60 * 60 * 1000) {
    console.log('PWA cleanup already run recently, skipping.');
    return {
      workersUnregistered: false,
      cachesCleared: false,
      localStorageCleared: false,
      alreadyRan: true
    };
  }
  
  // Proceed with full cleanup
  const workersUnregistered = await unregisterServiceWorkers();
  const cachesCleared = await clearTargetedCaches();
  const localStorageCleared = clearLocalStorageCache();
  
  // Mark that we've run the cleanup
  localStorage.setItem('pwa_cleanup_timestamp', now.toString());
  
  console.log(`PWA cleanup complete. 
    Service workers unregistered: ${workersUnregistered}, 
    Caches cleared: ${cachesCleared}, 
    localStorage cleared: ${localStorageCleared}`
  );
  
  return {
    workersUnregistered,
    cachesCleared,
    localStorageCleared,
    alreadyRan: false
  };
}

export default {
  unregisterServiceWorkers,
  clearTargetedCaches,
  clearLocalStorageCache,
  cleanupPWA
};
